import React from "react";
import { FileText, Zap } from "lucide-react";
import { ReconnectionRequest } from "../../../types";
import { useParams } from "react-router-dom";
import { useMeterHistory } from "@features/cx/reconnect/hooks";
import { Badge } from "@shared/ui/badge";
import { cn } from "@shared/lib/utils";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

// Mocked types for services and SLA summary
export interface ServiceInfo {
  serviceType: string;
  meterNumber: string;
  deviceNumber: string;
  meterType: string;
  currentReading: string;
  readingDate: string;
}

export interface SlaSummary {
  slaTarget: {
    acknowledgementTime: string;
    responseTime: string;
    resolutionTime: string;
  };
  slaAchieved: {
    acknowledgementTime: string;
    responseTime: string;
    resolutionTime: string;
  };
}

interface ReconnectDetailsTabProps {
  request: ReconnectionRequest & {
    services: ServiceInfo[];
    slaSummary: SlaSummary;
  };
}

const cardClass = "border border-gray-200 p-4 rounded-md";

const ReconnectDetailsTab: React.FC<ReconnectDetailsTabProps> = ({
  request,
}) => {
  const { action } = useParams();
  const { data: meterHistory } = useMeterHistory(request.consumer?.id, action);

  // Helper for NA
  const get = (val: any) =>
    val === undefined || val === null || val === "" ? "NA" : val;

  // Selection Mappings
  const accountStructureLabels: Record<string, string> = {
    same_master: "Keep under the same master account",
    new_master: "Link to a different master account",
    individual: "Reconnect as an individual account",
    link_different: "Link to a different master account",
    link_different_owner: "Link to a different owner account",
    keep_same_master: "Keep under the same master account",
    link_master: "Link to master account",
  };

  const serviceAreaLabels: Record<string, string> = {
    same_area: "Same Service Area",
    new_area: "New Service Area",
    same: "Existing Service Area",
    new: "New Area Requested",
  };

  const mailingPrefLabels: Record<string, string> = {
    same_as_service: "Same as Service Address",
    same: "Same as Service Address",
    different: "Different Billing Address",
  };

  const timeSlotLabels: Record<string, string> = {
    "0": "Morning (8AM - 12PM)",
    "1": "Afternoon (12PM - 4PM)",
    "2": "Evening (4PM - 8PM)",
  };

  // Support both camelCase and snake_case API structures without TS compiler issues
  const req = request as any;
  const additionalData = (req.additionalData || req.additional_data || {}) as any;
  const accountStructure = additionalData.accountStructure || additionalData.account_structure || {};
  const serviceAddress = additionalData.serviceAddress || additionalData.service_address || {};
  const mailingAddress = additionalData.mailingAddress || additionalData.mailing_address || {};

  const accountPref = get(
    req.accountStructurePreference ||
      accountStructure.choice ||
      additionalData.accountStructurePreference,
  );

  const serviceAreaPref = get(
    req.serviceAreaPreference ||
      serviceAddress.type ||
      additionalData.serviceAreaPreference,
  );

  const mailingPref = get(
    req.mailingAddressPreference ||
      mailingAddress.type ||
      additionalData.mailingAddressPreference,
  );

  // Formatter for address details using camelCase
  const formatAddressDetail = (
    unit?: string,
    street?: string,
    city?: string,
    zip?: string,
  ) => {
    return [unit ? `${unit}` : "", street, city, zip]
      .filter(Boolean)
      .join(", ");
  };

  const newServiceAreaDetail = formatAddressDetail(
    serviceAddress.houseUnit || serviceAddress.house_unit,
    serviceAddress.street,
    serviceAddress.city,
    serviceAddress.zip,
  );

  const differentMailingDetail = formatAddressDetail(
    mailingAddress.houseUnit || mailingAddress.house_unit,
    mailingAddress.street,
    mailingAddress.city,
    mailingAddress.zip,
  );

  const newMasterAccountNo =
    accountStructure.newMasterAccountNo ||
    accountStructure.new_master_account_no;
  const accountPrefValue = accountStructureLabels[accountPref] || accountPref;
  const accountPrefDisplay = newMasterAccountNo
    ? `${accountPrefValue} (${newMasterAccountNo})`
    : accountPrefValue;

  const serviceAreaDisplay = (
    <div>
      <div>{serviceAreaLabels[serviceAreaPref] || serviceAreaPref}</div>
      {(serviceAreaPref === "new" || serviceAreaPref === "new_area") &&
        newServiceAreaDetail && (
          <div className="text-xs text-gray-500 mt-1 font-normal leading-relaxed">
            {newServiceAreaDetail}
          </div>
        )}
    </div>
  );

  const mailingPrefDisplay = (
    <div>
      <div>{mailingPrefLabels[mailingPref] || mailingPref}</div>
      {mailingPref === "different" && differentMailingDetail && (
        <div className="text-xs text-gray-500 mt-1 font-normal leading-relaxed">
          {differentMailingDetail}
        </div>
      )}
    </div>
  );

  const preferredTimeSlot =
    additionalData.preferredTimeSlot || additionalData.preferred_time_slot;
  const preferredTimeDisplay = get(
    req.preferredTimeSlotDisplay ||
      req.prefferedTimeSlotDisplay ||
      req.preffered_time_slot_display ||
      req.preferred_time_slot_display ||
      (preferredTimeSlot !== undefined
        ? timeSlotLabels[preferredTimeSlot]
        : undefined),
  );

  // SLA Data
  const defaultSla = {
    acknowledgementTime: "-",
    responseTime: "-",
    resolutionTime: "-",
  };
  const slaTarget = request.slaSummary?.slaTarget || defaultSla;
  const slaAchieved = request.slaSummary?.slaAchieved || defaultSla;

  // Last Assigned Meter Logic
  const lastAssignedMeter = meterHistory
    ?.filter((m: any) => m.meterStatus === "Assigned")
    ?.sort(
      (a: any, b: any) =>
        new Date(b.allocationDate || 0).getTime() -
        new Date(a.allocationDate || 0).getTime(),
    )[0];

  return (
    <div className="space-y-4">
      {/* Reconnection Details Card */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded-lg">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="font-bold text-base text-gray-900">
            Reconnection Details
          </h2>
        </div>
        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ValueLabel
              label="Request ID"
              value={get(request.requestNo)}
              tooltip="The unique identifier assigned to this reconnection request."
            />
            <ValueLabel
              label="Reason"
              value={get(request.requestType)}
              tooltip="The specific reason provided for requesting a reconnection."
            />
            <ValueLabel
              label="Reason Code"
              value={get(request.utilitySupportRequest?.configurationCode)}
              tooltip="Internal system code for the reconnection category."
            />
            <ValueLabel
              label="Priority"
              value={get(request.priorityLevelDisplay)}
              tooltip="The urgency level assigned to this service request."
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ValueLabel
              label="Preferred Date"
              value={get(request.requestDate)}
              tooltip="The specific date requested by the customer for the reconnection."
            />
            <ValueLabel
              label="Preferred Time"
              value={preferredTimeDisplay}
              tooltip="The time window preferred by the customer for the service appointment."
            />
            <div className="col-span-2">
              <ValueLabel
                label="Service Address"
                value={get(request.consumer?.serviceAddress)}
                tooltip="The physical address where the reconnection service will be performed."
              />
            </div>
          </div>

          <div>
            <ValueLabel
              label="Description"
              value={get(request.utilitySupportRequest?.longDescription)}
              tooltip="Additional context and details regarding the reconnection request."
              className="text-gray-600 leading-relaxed max-w-4xl"
            />
          </div>

          <div>
            <ValueLabel
              label="Notes"
              value={get(request.consumerRemark)}
              tooltip="Internal remarks or customer-provided notes for the field team."
              className="text-gray-600"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-gray-100">
            <div className="col-span-1">
              <ValueLabel
                label="Account Structure Preference"
                value={accountPrefDisplay}
                tooltip="The preferred account linkage (e.g., Master vs. Individual) after reconnection."
              />
            </div>
            <div className="col-span-1">
              <ValueLabel
                label="Service Area Selection"
                value={serviceAreaDisplay}
                tooltip="The specific service zone or geographic area selected for this account."
              />
            </div>
            <div className="col-span-1">
              <ValueLabel
                label="Mailing Address"
                value={mailingPrefDisplay}
                tooltip="The preferred mailing address choice for the billing statements."
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
        {/* SLA Target */}
        <div className={cardClass}>
          <p className="card-heading-text mb-4">SLA Target</p>
          <div className="grid grid-cols-3 w-full gap-4">
            <ValueLabel
              label="Acknowledgement Time"
              value={get(slaTarget.acknowledgementTime)}
            />
            <ValueLabel
              label="Response Time"
              value={get(slaTarget.responseTime)}
            />
            <ValueLabel
              label="Resolution Time"
              value={get(slaTarget.resolutionTime)}
            />
          </div>
        </div>

        {/* SLA Achieved */}
        <div className={cardClass}>
          <p className="card-heading-text mb-4">SLA Achieved</p>
          <div className="grid grid-cols-3 w-full gap-4">
            <ValueLabel
              label="Acknowledgement Time"
              value={get(slaAchieved.acknowledgementTime)}
              className={
                get(slaAchieved.acknowledgementTime) !== "Pending"
                  ? "text-green-600"
                  : ""
              }
            />
            <ValueLabel
              label="Response Time"
              value={get(slaAchieved.responseTime)}
              className={
                get(slaAchieved.responseTime) !== "Pending"
                  ? "text-blue-600"
                  : ""
              }
            />
            <ValueLabel
              label="Resolution Time"
              value={get(slaAchieved.resolutionTime)}
            />
          </div>
        </div>
      </div>

      {/* Allocation History */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-50 rounded-lg">
              <Zap className="h-4 w-4 text-orange-400 fill-orange-400" />
            </div>
            <h2 className="font-bold text-base text-gray-900">
              Allocation History
            </h2>
          </div>
          <span className="text-[11px] font-medium text-gray-400">
            Water Meter
          </span>
        </div>

        {lastAssignedMeter ? (
          <div className="bg-[#fcfdfe] border border-gray-100 border-l-4 border-l-blue-600 rounded-lg p-5 relative transition-all duration-300">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-sm font-bold text-gray-900">
                {lastAssignedMeter.meterNumber}
              </h3>
              <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-none px-4 py-1 rounded-full text-[10px] font-bold uppercase">
                {lastAssignedMeter.meterStatus}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
              <ValueLabel
                label="DEVICE NUMBER"
                value={lastAssignedMeter.deviceNumber}
              />
              <ValueLabel
                label="METER TYPE"
                value={lastAssignedMeter.meterType}
              />
              <ValueLabel
                label="UTILITY SERVICE"
                value={lastAssignedMeter.utilityService}
              />
              <ValueLabel
                label="CURRENT READING"
                value={lastAssignedMeter.lastReading}
              />
              <ValueLabel
                label="READING DATE"
                value={lastAssignedMeter.lastReadingDate}
              />
              <ValueLabel
                label="STATUS"
                value={
                  <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-none px-4 py-1 rounded-full text-[10px] font-bold uppercase mt-1">
                    {lastAssignedMeter.meterStatus}
                  </Badge>
                }
              />
              <ValueLabel
                label="ASSIGNED DATE"
                value={lastAssignedMeter.allocationDate}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400 bg-gray-50/30 rounded-xl border border-dashed border-gray-200">
            <Zap className="h-10 w-10 mb-3 opacity-20" />
            <p className="text-sm font-medium">
              No assigned meter found in history
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconnectDetailsTab;
